import React from 'react';
import { Button, ButtonVariant } from '../../common/components/Button/Button';
import { Modal } from '../../common/components/Modal';
import { Info } from 'lucide-react';
import styles from './NotificationModal.module.scss';

export type NotificationType = 'success' | 'error' | 'info' | 'warning';

export interface NotificationButton {
    label: string;
    onClick: () => void;
    variant?: ButtonVariant;
    style?: React.CSSProperties;
    isLoading?: boolean;
}

export interface NotificationModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    message: string | React.ReactNode;
    type?: NotificationType;
    buttons?: NotificationButton[];
    body?: React.ReactNode;
}

/**
 * NotificationModal component for displaying modal notifications
 * with different types (success, error) and customizable content
 */
const NotificationModal: React.FC<NotificationModalProps> = ({
    isOpen,
    onClose,
    title,
    message,
    type = 'success',
    body,
    buttons = [{ label: 'OK', onClick: onClose, isLoading: false }],
}) => {
    // SVG icons based on notification type
    const renderIcon = (): React.ReactElement => {
        switch (type) {
            case 'success':
                return (
                    <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='white'
                        strokeWidth='3'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                    >
                        <polyline points='20 6 9 17 4 12'></polyline>
                    </svg>
                );
            case 'error':
                return (
                    <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='white'
                        strokeWidth='3'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                    >
                        <circle cx='12' cy='12' r='10'></circle>
                        <line x1='12' y1='8' x2='12' y2='12'></line>
                        <line x1='12' y1='16' x2='12.01' y2='16'></line>
                    </svg>
                );
            case 'info':
                return <Info />;
            default:
                return (
                    <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='white'
                        strokeWidth='3'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                    >
                        <circle cx='12' cy='12' r='10'></circle>
                        <line x1='12' y1='16' x2='12' y2='12'></line>
                        <line x1='12' y1='8' x2='12.01' y2='8'></line>
                    </svg>
                );
        }
    };

    const renderTitle = () => (
        <div className={styles.titleContent}>
            <div className={styles.notificationIcon}>{renderIcon()}</div>
            <span>{title}</span>
        </div>
    );

    const renderFooter = () => (
        <div className={styles.notificationFooter}>
            {buttons.map((button, index) => (
                <Button
                    key={index}
                    variant={button.variant || 'primary'}
                    onClick={button.onClick}
                    style={button.style}
                    size='md'
                    isLoading={button.isLoading}
                >
                    {button.label}
                </Button>
            ))}
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size='sm'
            title={renderTitle()}
            footer={renderFooter()}
            showDefaultFooter={false}
            headerClassName={`${styles.headerOverride} ${styles.notificationHeader} ${styles[type || 'success']}`}
        >
            {typeof message === 'string' ? <p style={{ paddingTop: '16px' }}>{message}</p> : message}
            {body}
        </Modal>
    );
};

export default NotificationModal;
